import { useEffect } from 'react';
import { useUnlockEditProfile } from '../../../utils/useUnlockEditProfile';
import { useHandleProfileLock } from '../../../utils/useHandleProfileLock';

/**
 * Custom hook to manage profile locking for supervision editing
 *
 * This hook handles the locking mechanism to prevent concurrent editing of supervision profiles.
 * When the purpose is 'edit', it locks the profile and sets up cleanup to unlock it when
 * the component unmounts or when the user navigates away from the page.
 *
 * @hook
 *
 * @param {Object} params - The parameters object
 * @param {string} params.purpose - The purpose of the hook usage ('edit' or other)
 * @param {string|number} params.id - The supervision ID to lock/unlock
 *
 * @returns {void} This hook doesn't return anything, it only manages side effects
 *
 * @example
 * // In a supervision editing component
 * useSupervisionProfileLock({
 *   purpose: 'edit',
 *   id: supervisionId
 * });
 *
 * @see {@link useUnlockEditProfile} for the unlock functionality
 * @see {@link useHandleProfileLock} for the lock functionality
 */
export const useSupervisionProfileLock = ({ purpose, id }) => {
  const { unlockEditProfile } = useUnlockEditProfile(id, 'supervisions');
  const { handleProfileLock } = useHandleProfileLock(id, 'supervisions');

  useEffect(() => {
    if (purpose === 'edit') {
      (async () => {
        await handleProfileLock();
      })();

      const handleBeforeUnload = () => {
        unlockEditProfile();
      };

      globalThis.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        // clean up the event handler when the component unmounts
        globalThis.removeEventListener('beforeunload', handleBeforeUnload);
        // call the unlock func when the component unmounts
        unlockEditProfile();
      };
    }
    return undefined;
  }, [purpose, handleProfileLock, unlockEditProfile]);
};
